import type { DocMeta, TiptapDoc } from "@klarity/schema";
import { openDB } from "idb";

export type Draft = {
  docId: string;
  baseVersion: number;
  title: string;
  meta: DocMeta;
  content: TiptapDoc;
  savedAt: string;
};

export type DraftStore = {
  get(docId: string): Promise<Draft | undefined>;
  put(draft: Draft): Promise<void>;
  delete(docId: string): Promise<void>;
};

export function idbDraftStore(dbName = "klarity-docs"): DraftStore {
  const db = openDB(dbName, 1, {
    upgrade(d) {
      d.createObjectStore("drafts", { keyPath: "docId" });
    },
  });
  return {
    get: async (docId) => (await (await db).get("drafts", docId)) as Draft | undefined,
    put: async (draft) => {
      await (await db).put("drafts", draft);
    },
    delete: async (docId) => {
      await (await db).delete("drafts", docId);
    },
  };
}

export function memoryDraftStore(): DraftStore {
  const map = new Map<string, Draft>();
  return {
    get: async (docId) => {
      const d = map.get(docId);
      return d ? structuredClone(d) : undefined;
    },
    put: async (draft) => {
      map.set(draft.docId, structuredClone(draft));
    },
    delete: async (docId) => {
      map.delete(docId);
    },
  };
}

let fallback: DraftStore | null = null;

/** IndexedDB when available; memory otherwise (private windows can refuse IndexedDB). */
export function defaultDraftStore(): DraftStore {
  const idb = idbDraftStore();
  if (!fallback) fallback = memoryDraftStore();
  const mem = fallback;
  const guard =
    <A extends unknown[], R>(f: (s: DraftStore) => (...a: A) => Promise<R>) =>
    async (...a: A): Promise<R> => {
      try {
        return await f(idb)(...a);
      } catch {
        return f(mem)(...a);
      }
    };
  return { get: guard((s) => s.get), put: guard((s) => s.put), delete: guard((s) => s.delete) };
}

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => [k, stable(v)]),
    );
  }
  return value;
}

export function sameContent(a: unknown, b: unknown): boolean {
  return JSON.stringify(stable(a)) === JSON.stringify(stable(b));
}
